import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, Row, Col, Table, Badge, Form, Button } from 'react-bootstrap';
import { FaDollarSign, FaPlus, FaExclamationTriangle, FaCheckCircle, FaPrint, FaHistory, FaTimes } from 'react-icons/fa';

const DoctorPaymentHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [salaries, setSalaries] = useState([]);
  const [historyYear, setHistoryYear] = useState(new Date().getFullYear());
  const [fromProfile, setFromProfile] = useState(false);
  const [loading, setLoading] = useState(true);

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    // Check if coming from profile
    const urlParams = new URLSearchParams(window.location.search);
    setFromProfile(urlParams.get('from') === 'profile');
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [doctorRes, salariesRes] = await Promise.all([
        axios.get(`/api/doctors.php?id=${id}`),
        axios.get(`/api/doctor-salaries.php?doctorId=${id}`)
      ]);
      setDoctor(doctorRes.data);
      setSalaries(salariesRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-PK', { style: 'currency', currency: 'PKR' });
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <h3>Loading...</h3>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="p-4 text-center">
        <h3>Doctor not found</h3>
        <Button variant="secondary" onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  const doctorSalaries = salaries.filter(s => s.month.startsWith(historyYear.toString()));
  const totalPaid = doctorSalaries.reduce((sum, s) => sum + parseFloat(s.amount || 0), 0);
  const totalAllowances = doctorSalaries.reduce((sum, s) => sum + parseFloat(s.allowances || 0), 0);
  const totalDeductions = doctorSalaries.reduce((sum, s) => sum + parseFloat(s.deductions || 0), 0);
  const netTotal = totalPaid + totalAllowances - totalDeductions;

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2><FaHistory className="me-2" />Payment History</h2>
        <div>
          <Button variant="primary" className="me-2" onClick={() => window.print()}>
            <FaPrint className="me-1" /> Print History
          </Button>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            <FaTimes className="me-1" /> Close
          </Button>
        </div>
      </div>

      <Card className="mb-4 border-0 shadow-sm" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
        <Card.Body className="text-white">
          <Row>
            <Col md={8}>
              <h5 className="fw-bold">Dr. {doctor.name}</h5>
              <p className="mb-1">Employee ID: DOC-{doctor.id}</p>
              <p className="mb-1">Subject: {doctor.specialization}</p>
              <p className="mb-0">Base Salary: {formatCurrency(doctorSalaries[0]?.amount || 0)}</p>
            </Col>
            <Col md={4} className="text-end">
              <p className="mb-1">Total Paid ({historyYear})</p>
              <h3 className="fw-bold">{formatCurrency(netTotal)}</h3>
              <p className="mb-0">{doctorSalaries.length} payments made</p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Form.Group className="mb-4" style={{maxWidth: '300px'}}>
        <Form.Label className="fw-bold">Filter by Year</Form.Label>
        <Form.Select value={historyYear} onChange={(e) => setHistoryYear(parseInt(e.target.value))}>
          {Array.from({length: 10}, (_, i) => new Date().getFullYear() - 5 + i).map(year => <option key={year} value={year}>{year}</option>)}
        </Form.Select>
      </Form.Group>

      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center py-3">
            <Card.Body>
              <FaDollarSign size={40} className="text-success mb-2" />
              <h4 className="mb-0 fw-bold">{formatCurrency(totalPaid)}</h4>
              <p className="text-muted mb-0 small">Total Paid</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center py-3">
            <Card.Body>
              <FaPlus size={40} className="text-info mb-2" />
              <h4 className="mb-0 fw-bold text-success">{formatCurrency(totalAllowances)}</h4>
              <p className="text-muted mb-0 small">Total Allowances</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center py-3">
            <Card.Body>
              <FaExclamationTriangle size={40} className="text-warning mb-2" />
              <h4 className="mb-0 fw-bold text-danger">{formatCurrency(totalDeductions)}</h4>
              <p className="text-muted mb-0 small">Total Deductions</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center py-3">
            <Card.Body>
              <FaCheckCircle size={40} className="text-primary mb-2" />
              <h4 className="mb-0 fw-bold">{doctorSalaries.length}</h4>
              <p className="text-muted mb-0 small">Total Payments</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white py-3">
          <h6 className="mb-0 fw-bold">📋 Payment History - {historyYear}</h6>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive className="mb-0">
            <thead className="bg-dark text-white">
              <tr>
                <th className="py-3">Payment Date</th>
                <th className="py-3">Month/Year</th>
                <th className="py-3">Basic Salary</th>
                <th className="py-3">Allowances</th>
                <th className="py-3">Deductions</th>
                <th className="py-3">Net Salary</th>
                <th className="py-3">Payment Method</th>
                <th className="py-3">Remarks</th>
                <th className="py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {doctorSalaries.map((sal) => {
                const netSalary = parseFloat(sal.amount || 0) + parseFloat(sal.allowances || 0) - parseFloat(sal.deductions || 0);
                return (
                  <tr key={sal.id}>
                    <td className="py-3">{sal.payDate ? new Date(sal.payDate).toLocaleDateString('en-GB') : new Date(sal.createdAt).toLocaleDateString('en-GB')}</td>
                    <td className="py-3">{new Date(sal.month + '-01').toLocaleDateString('en-GB', { year: 'numeric', month: 'long' })}</td>
                    <td className="py-3 fw-bold">{formatCurrency(sal.amount)}</td>
                    <td className="py-3 text-success">+{formatCurrency(sal.allowances || 0)}</td>
                    <td className="py-3 text-danger">-{formatCurrency(sal.deductions || 0)}</td>
                    <td className="py-3 fw-bold text-primary">{formatCurrency(netSalary)}</td>
                    <td className="py-3"><Badge bg="secondary">{sal.paymentMethod || 'Cash'}</Badge></td>
                    <td className="py-3">{sal.notes || '-'}</td>
                    <td className="py-3 text-center">
                      <Button variant="info" size="sm" onClick={() => {
                        const printWindow = window.open('', '', 'width=800,height=600');
                        const netSalary = parseFloat(sal.amount || 0) + parseFloat(sal.allowances || 0) - parseFloat(sal.deductions || 0);
                        printWindow.document.write(`
                          <html>
                            <head>
                              <title>Salary Receipt - Dr. ${doctor.name}</title>
                              <style>
                                @page { size: A4; margin: 0; }
                                html, body { height: 100%; margin: 0; padding: 0; }
                                body { font-family: Arial, sans-serif; padding: 20px; position: relative; min-height: 100vh; display: flex; flex-direction: column; width: 210mm; height: 297mm; margin: 0 auto; box-sizing: border-box; }
                                .content { flex: 1; position: relative; }
                                .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
                                .info { margin: 20px 0; }
                                .info-row { display: flex; justify-content: space-between; margin: 10px 0; }
                                .label { font-weight: bold; }
                                table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                                th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
                                th { background-color: #333; color: white; }
                                .total { font-weight: bold; font-size: 18px; }
                                .footer { margin-top: auto; padding-top: 50px; text-align: center; font-size: 12px; color: #666; }
                                .paid-stamp { position: fixed; top: 40%; left: 50%; transform: translate(-50%, -50%) rotate(-25deg); border: 8px solid #28a745; color: #28a745; font-size: 80px; font-weight: bold; padding: 20px 40px; opacity: 0.3; border-radius: 15px; pointer-events: none; z-index: 1000; }
                              </style>
                            </head>
                            <body>
                              <div class="paid-stamp">PAID</div>
                              <div class="content">
                              <div class="header">
                                <h2>Salary Payment Receipt</h2>
                                <p>Doctor Payment Record</p>
                              </div>
                              <div class="info">
                                <div class="info-row">
                                  <span><span class="label">Doctor Name:</span> Dr. ${doctor.name}</span>
                                  <span><span class="label">Employee ID:</span> DOC-${doctor.id}</span>
                                </div>
                                <div class="info-row">
                                  <span><span class="label">Specialization:</span> ${doctor.specialization}</span>
                                  <span><span class="label">Payment Date:</span> ${sal.payDate ? new Date(sal.payDate).toLocaleDateString('en-GB') : new Date(sal.createdAt).toLocaleDateString('en-GB')}</span>
                                </div>
                                <div class="info-row">
                                  <span><span class="label">Month/Year:</span> ${new Date(sal.month + '-01').toLocaleDateString('en-GB', { year: 'numeric', month: 'long' })}</span>
                                  <span><span class="label">Payment Method:</span> ${sal.paymentMethod || 'Cash'}</span>
                                </div>
                              </div>
                              <table>
                                <tr>
                                  <th>Description</th>
                                  <th style="text-align: right;">Amount</th>
                                </tr>
                                <tr>
                                  <td>Basic Salary</td>
                                  <td style="text-align: right;">${formatCurrency(sal.amount)}</td>
                                </tr>
                                <tr>
                                  <td>Allowances</td>
                                  <td style="text-align: right; color: green;">+ ${formatCurrency(sal.allowances || 0)}</td>
                                </tr>
                                <tr>
                                  <td>Deductions</td>
                                  <td style="text-align: right; color: red;">- ${formatCurrency(sal.deductions || 0)}</td>
                                </tr>
                                <tr>
                                  <td class="total">Net Salary</td>
                                  <td class="total" style="text-align: right; color: blue;">${formatCurrency(netSalary)}</td>
                                </tr>
                              </table>
                              ${sal.notes ? `<div class="info"><span class="label">Remarks:</span> ${sal.notes}</div>` : ''}
                              </div>
                              <div class="footer">
                                <p>Generated on: ${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB')}</p>
                                <p>This is a computer-generated document. No signature required.</p>
                              </div>
                            </body>
                          </html>
                        `);
                        printWindow.document.close();
                        printWindow.print();
                      }}><FaPrint /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-light">
              <tr>
                <td colSpan="2" className="py-3 fw-bold">TOTALS:</td>
                <td className="py-3 fw-bold">{formatCurrency(totalPaid)}</td>
                <td className="py-3 fw-bold text-success">+{formatCurrency(totalAllowances)}</td>
                <td className="py-3 fw-bold text-danger">-{formatCurrency(totalDeductions)}</td>
                <td className="py-3 fw-bold text-primary">{formatCurrency(netTotal)}</td>
                <td colSpan="3"></td>
              </tr>
            </tfoot>
          </Table>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white py-3">
          <h6 className="mb-0 fw-bold">📅 Monthly Breakdown - {historyYear}</h6>
        </Card.Header>
        <Card.Body>
          <Row>
            {months.map((month, idx) => {
              const monthStr = `${historyYear}-${String(idx + 1).padStart(2, '0')}`;
              const monthSalary = doctorSalaries.find(s => s.month === monthStr);
              const isPaid = !!monthSalary;
              const amount = monthSalary ? parseFloat(monthSalary.amount || 0) + parseFloat(monthSalary.allowances || 0) - parseFloat(monthSalary.deductions || 0) : 0;
              
              return (
                <Col md={2} key={idx} className="mb-3">
                  <Card className={`text-center ${isPaid ? 'bg-primary text-white' : 'bg-secondary text-white'}`}>
                    <Card.Body className="py-3">
                      <h6 className="mb-1">{month.slice(0, 3)}</h6>
                      <p className="mb-0 small">{isPaid ? formatCurrency(amount) : 'Not Paid'}</p>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DoctorPaymentHistory;
